import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useSession } from "@clerk/clerk-react";
import { toast } from "@/lib/toast";
import {
  deleteExpress,
  listExpress,
  renameExpress,
  saveExpress,
  updateExpress,
  type DecodificarExpressRow,
  type SaveExpressInput,
} from "../api/express";

const QUERY_NAMESPACE = "decodificar-express" as const;

const SECOND = 1000;
const MINUTE = 60 * SECOND;

export const expressKeys = {
  all: [QUERY_NAMESPACE] as const,
  list: (limit: number) => [QUERY_NAMESPACE, "list", { limit }] as const,
};

export function useExpressHistory(limit = 30) {
  const { isAuthenticated } = useCurrentUser();
  const { client, isReady } = useSupabaseClient();

  return useQuery<DecodificarExpressRow[]>({
    queryKey: [...expressKeys.list(limit), isReady],
    enabled: isAuthenticated && isReady,
    queryFn: () => listExpress({ limit }, client),
    staleTime: 30 * SECOND,
    gcTime: 5 * MINUTE,
  });
}

export function useSaveExpress() {
  const qc = useQueryClient();
  const { isAuthenticated } = useCurrentUser();
  const { client } = useSupabaseClient();
  const { session } = useSession();

  return useMutation<DecodificarExpressRow, unknown, SaveExpressInput>({
    mutationFn: async (input) => {
      if (!isAuthenticated || !session) {
        throw new Error("Você precisa estar autenticado para salvar.");
      }
      return saveExpress(input, client);
    },
    onSuccess: () => {
      toast.success("Decodificação salva no histórico.");
      void qc.invalidateQueries({ queryKey: expressKeys.all });
    },
    onError: () => {
      toast.error("Não foi possível salvar a decodificação.");
    },
  });
}

export function useUpdateExpress() {
  const qc = useQueryClient();
  const { client } = useSupabaseClient();

  return useMutation<DecodificarExpressRow, unknown, { id: string } & SaveExpressInput>({
    mutationFn: async ({ id, ...input }) => updateExpress(id, input, client),
    onSuccess: () => {
      toast.success("Decodificação atualizada.");
      void qc.invalidateQueries({ queryKey: expressKeys.all });
    },
    onError: () => {
      toast.error("Não foi possível atualizar a decodificação.");
    },
  });
}

export function useDeleteExpress() {
  const qc = useQueryClient();
  const { client } = useSupabaseClient();

  return useMutation<void, unknown, string>({
    mutationFn: async (id) => deleteExpress(id, client),
    onSuccess: () => {
      toast.success("Removido do histórico.");
      void qc.invalidateQueries({ queryKey: expressKeys.all });
    },
    onError: () => {
      toast.error("Não foi possível remover.");
    },
  });
}

export function useRenameExpress() {
  const qc = useQueryClient();
  const { client } = useSupabaseClient();

  return useMutation<
    DecodificarExpressRow,
    unknown,
    { id: string; patientLabel: string }
  >({
    mutationFn: async ({ id, patientLabel }) =>
      renameExpress(id, patientLabel, client),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expressKeys.all });
    },
    onError: () => {
      toast.error("Não foi possível renomear.");
    },
  });
}
